<script setup lang="ts">
import { InterfacePanelState } from "~/classes/generated/interface"

/**
 * In this component, the conversation id is guaranteed to exist. We key this component by the conversation id as well to ensure that it fully recreates in the case of a new conversation
 */
const props = defineProps({
  conversationId: null
})

const { panelAgentId } = useGlobals()
const { deleteConversation, mergedTranscript, getReactiveConversation } = useConversations()
const { showPanelAndPersist } = usePanel()
const { state: myself } = useSelf()
const { findUserIdByEmail } = useSavedUsers()
const { notifyError } = useAlerts()
const { onCreateOrOpenLatestConversationIntent, onSwitchFromPanelToMainViewIntent } = useAgent()

const isEditingTitle = ref(false)
const titleInput = useTemplateRef("titleInput")

const reactiveConversation = getReactiveConversation(props.conversationId, "agent", true)!
if (!reactiveConversation)
  throw new Error(`No conversation found for id ${props.conversationId}`)

const conversation = reactiveConversation.ref()

const transcript = computed(() => mergedTranscript(conversation.value))

const isOwner = computed(() => {
  if (!myself.value.user)
    return false
  return conversation.value.getUserRole(myself.value.user.id)?.isOwner() ?? false
})
const inviteUser = ref("")
const inviteLoading = ref(false)

// TODO: watch for navigating to full panel agent and if we do that, switch to the
// chats sidebar
async function onNewChatClick(toggle?: () => void) {
  if (toggle)
    toggle()
  return onCreateOrOpenLatestConversationIntent()
}

async function startEditingTitle(toggle?: () => void) {
  isEditingTitle.value = true
  if (toggle)
    toggle()
  await nextTick()
  if (!titleInput.value)
    return
  titleInput.value.focus()
  titleInput.value.value = conversation.value.title() ?? ""
}

function finishEditingTitle() {
  if (!isEditingTitle.value)
    return
  isEditingTitle.value = false
  if (titleInput.value) {
    titleInput.value.blur()
  }

  const newTitle = titleInput.value?.value.trim()
  if (!newTitle || !conversation.value.inner())
    return

  reactiveConversation.updateConversationTitle(newTitle)
}

function onViewAllChatsClick(toggle?: () => void) {
  if (toggle)
    toggle()
  showPanelAndPersist(InterfacePanelState.Chats)
}

function onClickOpenMainView(toggle?: () => void) {
  if (toggle)
    toggle()
  onSwitchFromPanelToMainViewIntent()
}

async function onClickDelete(toggle?: () => void) {
  if (toggle)
    toggle()
  deleteConversation(props.conversationId)
  await onCreateOrOpenLatestConversationIntent()
}

function resetInvite() {
  inviteUser.value = ""
  inviteLoading.value = false
}

async function inviteUserToConversation() {
  if (!inviteUser.value.trim()) {
    console.warn("No user to invite")
    return resetInvite()
  }

  inviteLoading.value = true
  const userId = await findUserIdByEmail(inviteUser.value.trim())
  if (!userId) {
    notifyError(`User with email ${inviteUser.value} not found`, "Please check the email and try again")
    return resetInvite()
  }

  try {
    await conversation.value.sendInvite(userId)
  }
  catch (error) {
    console.error("Failed to invite user to conversation:", error)
    notifyError("Failed to invite user", "Please try again later")
  }
  finally {
    resetInvite()
  }
}

async function removeUserFromConversation(userId: string) {
  inviteLoading.value = true
  try {
    await conversation.value.sendInvite(userId, true)
  }
  catch (error) {
    console.error("Failed to remove user from conversation:", error)
    notifyError("Failed to remove user", "Please try again later")
  }
}
</script>

<template>
  <div class="panel-agent-outer">
    <div class="panel-agent-header">
      <div class="left">
        <dropdown
          anchor-x="right"
          anchor-y="bottom"
          flow-x="left"
          flow-y="down"
        >
          <template #default="{ toggle, active }">
            <div v-if="isEditingTitle" class="inline-select-button">
              <input
                ref="titleInput"
                class="input-inner"
                type="text"
                placeholder="Enter chat title..."
                @blur="finishEditingTitle()"
                @keyup.enter="finishEditingTitle()"
              >
            </div>
            <div
              v-else
              class="inline-select-button"
              :class="{ active }"
              @click="toggle"
            >
              <div v-if="conversation.title()" class="title">
                {{ conversation.title() }}
              </div>
              <div v-else>
                <span class="untitled">Untitled</span>
              </div>
              <icon-action
                type="chevron-down"
                size="xs"
                class="chevron-down"
              />
            </div>
          </template>
          <template #menu="{ toggle }">
            <dropdown-menu class="inline-select-container">
              <menu-section>
                <dropdown-item @click="startEditingTitle(toggle)">
                  <template #icon>
                    <icon-action size="xs" type="rename" />
                  </template>
                  <template #default>
                    <div>Rename...</div>
                  </template>
                </dropdown-item>
                <dropdown-item opposite :disabled="!isOwner">
                  <template #default>
                    Invite others
                  </template>
                  <template #submenu>
                    <dropdown-sub-view class="invite-users-menu">
                      <div>
                        <dropdown-text-input
                          v-model="inviteUser"
                          placeholder="Add by e-mail"
                          @submit="inviteUserToConversation()"
                        />
                      </div>
                      <menu-separator />
                      <menu-section>
                        <div
                          v-for="role, userId in conversation.members()"
                          :key="userId"
                          class="invite-users-item group"
                        >
                          <user-item :user-id />
                          <div
                            v-if="!role.isOwner()"
                            class="invite-users-remove"
                            @click="removeUserFromConversation(userId)"
                          >
                            <icon-action
                              type="cross"
                              size="xs"
                              class="invite-users-cross"
                            />
                          </div>
                        </div>
                      </menu-section>
                    </dropdown-sub-view>
                  </template>
                </dropdown-item>
                <dropdown-item @click="onClickOpenMainView(toggle)">
                  <template #icon>
                    <icon-action size="xs" type="file-ext" />
                  </template>
                  <template #default>
                    <div>Open in full view</div>
                  </template>
                </dropdown-item>
              </menu-section>
              <menu-separator />
              <menu-section>
                <dropdown-item @click="onClickDelete(toggle)">
                  <template #icon>
                    <icon-action size="xs" type="archive" />
                  </template>
                  <template #default>
                    <div>Delete chat</div>
                  </template>
                </dropdown-item>
              </menu-section>
              <menu-separator />
              <menu-section>
                <dropdown-item @click="onNewChatClick(toggle)">
                  <template #icon>
                    <icon-action size="xs" type="file-add" />
                  </template>
                  <template #default>
                    <div>New chat</div>
                  </template>
                </dropdown-item>
                <dropdown-item @click="onViewAllChatsClick(toggle)">
                  View all chats
                </dropdown-item>
              </menu-section>
            </dropdown-menu>
          </template>
        </dropdown>
      </div>
      <div class="right" @click="onNewChatClick()">
        <icon-action
          type="plus"
          size="xs"
        />
      </div>
    </div>
    <menu-separator />
    <div :id="panelAgentId" class="panel-agent">
      <agent-transcript :transcript />
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.panel-agent-outer
  @apply flex flex-col h-full

.panel-agent-header
  @apply flex-none flex items-center justify-between
  @apply p-[14px]
  .left
    @apply flex items-center min-w-0 h-full w-full
    .untitled
      @apply text-$global-t-secondary font-italic
  .right
    @apply ml-auto
    @apply cursor-pointer
    @apply flex items-center justify-center flex-none
    @apply p-0.5 -my-0.5 rounded-[6px]
    &:hover
      @apply bg-[#{$menu-bg-item-hover}]

.panel-agent
  @apply h-full w-full min-h-0

.inline-select-container
  @apply min-w-40 mt-2 -mr-1
.inline-select-button
  @apply flex cursor-pointer items-center gap-1
  @apply -mx-1 -my-0.5 px-1 py-0.5 rounded-[#{$menu-br-item}]
  .title, .untitled
    @apply text-ellipses-inline
  &:hover, &.active
    @apply bg-[#{$menu-bg-item-hover}]
  .chevron-down
    @apply text-$global-t-secondary flex-none
.inline-select-dropdown
  @apply flex gap-[#{$menu-gap-items}] px-[#{$menu-px-default}] py-[#{$menu-py-default}] flex-row

.input-inner
  @apply placeholder-[#{$menu-t-input-placeholder}] bg-transparent
  @apply relative block w-full border-0 rounded-[6px] px-1
  @apply ring-none focus:ring-0 outline-none

.invite-users-menu
  @apply mr-5 w-70

.invite-users-item
  @apply flex items-center gap-2 w-full

.invite-users-remove
  @apply opacity-0 group-hover:opacity-100
  @apply text-$global-bg-icon-secondary
  @apply p-0.5 rounded-sm cursor-pointer
  &:hover
    @apply bg-$global-red-accent text-$global-red-primary
</style>
